import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import TrashButtonIcon from "shared/assets/Trash_Btn.svg";
import styles from "./SpeakerAssignRow.module.css";

function DragHandle() {
  return (
    <div className={styles.dragHandle} aria-label="Drag to reorder">
      <div className={styles.dragLine} />
      <div className={styles.dragLine} />
      <div className={styles.dragLine} />
    </div>
  );
}

function ChevronIcon({ open }) {
  return (
    <svg
      width="14"
      height="9"
      viewBox="0 0 14 9"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={classNames(styles.chevronIcon, {
        [styles.chevronIconOpen]: open,
      })}
      aria-hidden="true"
    >
      <path
        d="M12.414 8.328L6.914 2.828L1.414 8.328L0 6.914L6.914 0L13.828 6.914L12.414 8.328Z"
        fill="currentColor"
      />
    </svg>
  );
}

function SpeakerAssignRow({
  id,
  photo,
  name,
  variationOptions,
  selectedVariationIds,
  onSelectedVariationIdsChange,
  onRemove,
  draggable,
}) {
  const hasVariations = variationOptions.length > 0;
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(selectedVariationIds);
  const [thumbnailLoadFailed, setThumbnailLoadFailed] = useState(false);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const activeThumbnail = useMemo(() => {
    if (!hasVariations) {
      return photo;
    }

    const selectedVariations = variationOptions.filter((variation) =>
      selectedSet.has(variation.id),
    );

    if (selectedVariations.length === 1 && selectedVariations[0].photo) {
      return selectedVariations[0].photo;
    }

    return photo;
  }, [hasVariations, photo, selectedSet, variationOptions]);
  const activeName = useMemo(() => {
    if (!hasVariations) {
      return name;
    }

    const selectedVariations = variationOptions.filter((variation) =>
      selectedSet.has(variation.id),
    );

    if (selectedVariations.length === 1 && selectedVariations[0].label) {
      return selectedVariations[0].label;
    }

    return name;
  }, [hasVariations, name, selectedSet, variationOptions]);

  useEffect(() => {
    setThumbnailLoadFailed(false);
  }, [activeThumbnail]);

  useEffect(() => {
    setSelectedIds(
      Array.isArray(selectedVariationIds) ? selectedVariationIds : [],
    );
  }, [selectedVariationIds]);

  const hasRenderableThumbnail =
    typeof activeThumbnail === "string" &&
    activeThumbnail.trim().length > 0 &&
    !thumbnailLoadFailed;

  return (
    <div className={styles.rowGroup}>
      <div className={styles.row}>
        {hasRenderableThumbnail ? (
          <img
            className={styles.thumbnail}
            src={activeThumbnail}
            alt={activeName}
            onError={() => setThumbnailLoadFailed(true)}
          />
        ) : (
          <div
            className={styles.thumbnailFallback}
            aria-label="No speaker image"
          >
            No Image
          </div>
        )}

        <div className={styles.meta}>
          <div className={styles.nameWrap}>
            <span className={styles.name}>{activeName}</span>
            {hasVariations && (
              <button
                type="button"
                className={styles.chevronButton}
                aria-label={
                  isOpen ? "Collapse speaker options" : "Expand speaker options"
                }
                onClick={() => setIsOpen((current) => !current)}
              >
                <ChevronIcon open={isOpen} />
              </button>
            )}
          </div>
        </div>

        <div className={styles.actions}>
          <button
            className={styles.deleteButton}
            onClick={() => onRemove(id)}
            aria-label="Remove speaker"
            type="button"
          >
            <img src={TrashButtonIcon} alt="" className={styles.deleteIcon} />
          </button>
          {draggable && <DragHandle />}
        </div>
      </div>

      {hasVariations && isOpen && (
        <div className={styles.variationStrip}>
          {variationOptions.map((variation) => {
            const checked = selectedSet.has(variation.id);

            return (
              <button
                key={variation.id}
                type="button"
                className={styles.variationCard}
                onClick={() => {
                  setSelectedIds((current) => {
                    let nextIds = current;

                    if (current.includes(variation.id)) {
                      if (current.length === 1) {
                        nextIds = current;
                      } else {
                        nextIds = current.filter(
                          (idValue) => idValue !== variation.id,
                        );
                      }
                    } else {
                      nextIds = [...current, variation.id];
                    }

                    onSelectedVariationIdsChange(nextIds);
                    return nextIds;
                  });
                }}
              >
                {variation.photo ? (
                  <img
                    src={variation.photo}
                    alt={variation.label}
                    className={styles.variationThumb}
                  />
                ) : (
                  <div
                    className={styles.variationThumbFallback}
                    aria-label="No speaker image"
                  >
                    No Image
                  </div>
                )}
                <span className={styles.variationLabel}>{variation.label}</span>
                <span
                  className={classNames(styles.variationCheck, {
                    [styles.variationCheckActive]: checked,
                  })}
                  aria-hidden="true"
                >
                  {checked ? "✓" : ""}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

SpeakerAssignRow.propTypes = {
  id: PropTypes.string.isRequired,
  photo: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  variationOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      photo: PropTypes.string,
      label: PropTypes.string.isRequired,
    }),
  ),
  selectedVariationIds: PropTypes.arrayOf(PropTypes.string),
  onSelectedVariationIdsChange: PropTypes.func,
  onRemove: PropTypes.func,
  draggable: PropTypes.bool,
};

SpeakerAssignRow.defaultProps = {
  variationOptions: [],
  selectedVariationIds: [],
  onSelectedVariationIdsChange: () => {},
  onRemove: () => {},
  draggable: false,
};

export default SpeakerAssignRow;
